import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';

import PageHero from '../../MyComponents/LandingSections/PageHero';
import FeatureCards from '../../MyComponents/LandingSections/FeatureCards';
import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import HomePricing from '../Home/HomePricing/HomePricing';
import HomeContact from '../Home/HomeContact/HomeContact';
import { parseQuery } from '../../common/utils';
import { START_FREE_LINK, TUTORIAL_LINK, tutorialLinks } from '../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS, REDESIGN_TINTS } from '../../constants';

// Every card is a deep-link target: the rest of the app points here with
// `/features?feature=<key>` (see featureLinks in common/features.js).
const ANCHOR = 'feature-';

const TINT_CYCLE = [
    REDESIGN_TINTS.mint,
    REDESIGN_TINTS.amber,
    REDESIGN_TINTS.sky,
    REDESIGN_TINTS.peach,
    REDESIGN_TINTS.linen,
    REDESIGN_TINTS.apricot,
    REDESIGN_TINTS.periwinkle,
];

// Ordered as a lesson unfolds: the students, then the organisation around
// them, then the material, the instruments and finally the device.
// Every card shows a screenshot of the real app in the visitor's language, so
// `src` is `/redesign/<image>.<locale>.webp`, set in the component.
const FEATURES = [
    { key: 'individualBoards', image: 'feature-table-individuale', more: tutorialLinks.individualBoards, bullets: 4 },
    { key: 'control', image: 'features-control', more: tutorialLinks.groups },
    { key: 'audioVideo', image: 'features-call', more: tutorialLinks.audioVideo },
    { key: 'groups', image: 'features-groups', more: tutorialLinks.groups },
    { key: 'library', image: 'feature-biblioteca', more: tutorialLinks.library, bullets: 6 },
    { key: 'pdfDownload', image: 'features-pdf', more: tutorialLinks.saveIndividualBoards },
    { key: 'paste', image: 'feature-material-exercitiu', more: tutorialLinks.imageMenu, bullets: 4 },
    { key: 'specialLineage', image: 'features-lineage', more: tutorialLinks.rulingMenu, bullets: 5 },
    { key: 'geometry', image: 'features-geometry', more: tutorialLinks.geometry, bullets: 4 },
    { key: 'mathConvertor', image: 'features-converter', more: tutorialLinks.mathConvertor },
    { key: 'aiAssistant', image: 'feature-asistent-ai', more: tutorialLinks.aiAssistant },
    { key: 'mobile', image: 'features-mobile', more: tutorialLinks.phoneOptimization },
].map((feature, index) => ({
    ...feature,
    fit: 'cover',
    tint: TINT_CYCLE[index % TINT_CYCLE.length],
    mediaFirst: index % 2 === 0,
}));

const useStyles = makeStyles(() => ({
    index: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '8px',
    },
    indexLabel: {
        width: '100%',
        margin: '0 0 6px',
        textAlign: 'center',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.74rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: '40px',
        padding: '0 17px',
        border: `1px solid ${REDESIGN_COLORS.navBorder}`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.body,
        background: REDESIGN_COLORS.white,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
}));

const scrollToFeature = (key, behavior = 'smooth') => {
    const el = document.getElementById(`${ANCHOR}${key}`);
    if (el) el.scrollIntoView({ behavior, block: 'start' });
};

/**
 * The features page: one numbered card per feature, on the landing's
 * primitives, with a row of jump links in place of the old sticky summary
 * menu. `?feature=<key>` still scrolls to that feature, matched without
 * regard to case since the links around the app spell the keys both ways.
 */
const Features = () => {
    const classes = useStyles();
    const intl = useIntl();
    const features = FEATURES.map((feature) => ({ ...feature, src: `/redesign/${feature.image}.${intl.locale}.webp` }));
    const location = useLocation();

    useEffect(() => {
        const wanted = (parseQuery(location.search).feature || '').toLowerCase();
        if (!wanted) return undefined;
        const match = FEATURES.find((f) => f.key.toLowerCase() === wanted);
        if (!match) return undefined;
        // The cards reserve their height (16:9 frames), so a short delay for
        // the first paint is enough; no need to wait for images. The jump is
        // instant: a deep link should land on the card, not animate to it.
        const timer = setTimeout(() => scrollToFeature(match.key, 'auto'), 150);
        return () => clearTimeout(timer);
    }, [location.search]);

    return (
        <>
            <PageHero
                keyPrefix="redesign.features.hero"
                primaryTo={START_FREE_LINK}
                secondaryTo={TUTORIAL_LINK}
                trust={['count', 'browser', 'freePlan']}
                image={{ src: `/redesign/feature-material-exercitiu.${intl.locale}.webp`, width: 800, height: 450 }}
            />

            <LandingSection maxWidth={1080} paddingTop={0} paddingBottom={16}>
                <nav className={classes.index} aria-label="features">
                    <span className={classes.indexLabel}>
                        <FormattedMessage id="redesign.features.index.label" />
                    </span>
                    {FEATURES.map(({ key }) => (
                        <a
                            key={key}
                            href={`#${ANCHOR}${key}`}
                            className={classes.chip}
                            onClick={(event) => {
                                event.preventDefault();
                                scrollToFeature(key);
                            }}
                        >
                            <FormattedMessage id={`redesign.features.list.${key}.label`} />
                        </a>
                    ))}
                </nav>
            </LandingSection>

            <FeatureCards id="functii" keyPrefix="redesign.features.list" features={features} anchorPrefix={ANCHOR} />
            <HomePricing />
            <HomeContact keyPrefix="redesign.features.contact" to={START_FREE_LINK} />
        </>
    );
};

export default Features;
